import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBug, 
  faLightbulb, 
  faChartLine, 
  faQuestionCircle, 
  faFrown, 
  faCommentDots,
  faTimes,
  faUpload,
  faFileImage
} from '@fortawesome/free-solid-svg-icons';
import { api } from '../services/api';
import { useI18n } from '../hooks/useI18n';
import '../styles/FeedbackModal.css';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type FeedbackType = 'BUG_REPORT' | 'FEATURE_REQUEST' | 'IMPROVEMENT' | 'QUESTION' | 'COMPLAINT' | 'OTHER';

// Predefined categories
const CATEGORIES = [
  'Dashboard',
  'Items & Library',
  'Categories',
  'Notifications',
  'Settings',
  'Workspaces & Teams',
  'Authentication',
  'Performance',
  'User Interface',
  'Mobile Experience',
  'Data & Analytics',
  'Other'
];

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useI18n();
  const [type, setType] = useState<FeedbackType>('BUG_REPORT');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [screenshot, setScreenshot] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Capture comprehensive system info
  const [systemInfo] = useState({
    browser: navigator.userAgent,
    device: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
    os: navigator.platform,
    url: window.location.href,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    colorDepth: `${window.screen.colorDepth}-bit`,
    referrer: document.referrer || 'Direct',
    cookiesEnabled: navigator.cookieEnabled ? 'Yes' : 'No',
    onlineStatus: navigator.onLine ? 'Online' : 'Offline'
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const resetForm = () => {
    setType('BUG_REPORT');
    setTitle('');
    setDescription('');
    setCategory('');
    setScreenshot('');
    setAttachmentFile(null);
    setAttachmentPreview('');
    setError('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(t('feedback.fileTooLarge'));
        return;
      }

      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm'];
      if (!allowedTypes.includes(file.type)) {
        setError(t('feedback.invalidFileType'));
        return;
      }

      setAttachmentFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setAttachmentPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setAttachmentPreview('');
      }
    }
  };

  const removeAttachment = () => {
    setAttachmentFile(null);
    setAttachmentPreview('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim() || !description.trim()) {
      setError(t('feedback.titleDescRequired'));
      return;
    }

    if (!category) {
      setError(t('feedback.categoryRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      // For now, we'll send the file as base64 in screenshot field
      // In production, you'd upload to S3 or similar
      let screenshotData = screenshot.trim() || undefined;
      
      if (attachmentFile && attachmentPreview) {
        screenshotData = attachmentPreview;
      }

      const payload = {
        type,
        title: title.trim(),
        description: description.trim(),
        category: category,
        browser: `${systemInfo.browser} | Screen: ${systemInfo.screenResolution} | Viewport: ${systemInfo.viewportSize}`,
        device: `${systemInfo.device} | ${systemInfo.colorDepth} | ${systemInfo.onlineStatus}`,
        os: `${systemInfo.os} | ${systemInfo.language} | ${systemInfo.timezone}`,
        url: `${systemInfo.url} | Referrer: ${systemInfo.referrer}`,
        screenshot: screenshotData
      };

      console.log('Submitting feedback:', payload);

      await api.post('/feedback', payload);

      // Show success message
      setShowSuccess(true);
      
      // Auto-close after showing success
      setTimeout(() => {
        resetForm();
        setShowSuccess(false);
        onSuccess?.();
        onClose();
      }, 2500);
    } catch (err: any) {
      console.error('Feedback submission error:', err);
      setError(err.response?.data?.error || err.message || t('feedback.errorSubmit'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const typeOptions: { value: FeedbackType; label: string; icon: any; description: string }[] = [
    { value: 'BUG_REPORT', label: t('feedback.bugReport'), icon: faBug, description: t('feedback.bugReportDesc') },
    { value: 'FEATURE_REQUEST', label: t('feedback.featureRequest'), icon: faLightbulb, description: t('feedback.featureRequestDesc') },
    { value: 'IMPROVEMENT', label: t('feedback.improvement'), icon: faChartLine, description: t('feedback.improvementDesc') },
    { value: 'QUESTION', label: t('feedback.question'), icon: faQuestionCircle, description: t('feedback.questionDesc') },
    { value: 'COMPLAINT', label: t('feedback.complaint'), icon: faFrown, description: t('feedback.complaintDesc') },
    { value: 'OTHER', label: t('feedback.other'), icon: faCommentDots, description: t('feedback.otherDesc') }
  ];

  return (
    <div className="feedback-modal-overlay" onClick={onClose}>
      <div className="feedback-modal-content" onClick={(e) => e.stopPropagation()}>
        {showSuccess ? (
          <div className="feedback-success-message">
            <div className="feedback-success-icon">✓</div>
            <h2>{t('feedback.successTitle')}</h2>
            <p>{t('feedback.successMessage')}</p>
          </div>
        ) : (
        <>
        <div className="feedback-modal-header">
          <h2>{t('feedback.title')}</h2>
          <button className="feedback-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="feedback-form">
          {error && <div className="feedback-error">{error}</div>}

          <div className="form-group">
            <label>{t('feedback.typeLabel')}</label>
            <div className="feedback-type-grid">
              {typeOptions.map(option => (
                <div
                  key={option.value}
                  className={`feedback-type-card ${type === option.value ? 'selected' : ''}`}
                  onClick={() => setType(option.value)}
                >
                  <div className="type-icon">
                    <FontAwesomeIcon icon={option.icon} />
                  </div>
                  <span className="type-label">{option.label}</span>
                  <span className="type-description">{option.description}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="title">{t('feedback.titleLabel')}</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('feedback.titlePlaceholder')}
              maxLength={200}
              required
            />
            <span className="char-count">{title.length}/200</span>
          </div>

          <div className="form-group">
            <label htmlFor="description">{t('feedback.descriptionLabel')}</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('feedback.descriptionPlaceholder')}
              rows={6}
              maxLength={2000}
              required
            />
            <span className="char-count">{description.length}/2000</span>
          </div>

          <div className="form-group">
            <label htmlFor="category">{t('feedback.categoryLabel')}</label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            >
              <option value="">{t('feedback.categoryPlaceholder')}</option>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{t(`feedback.category.${cat}`)}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="attachment">{t('feedback.attachmentLabel')}</label>
            <div className="file-upload-section">
              {!attachmentFile ? (
                <label className="file-upload-button">
                  <FontAwesomeIcon icon={faUpload} />
                  <span>{t('feedback.uploadBtn')}</span>
                  <input
                    type="file"
                    id="attachment"
                    accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </label>
              ) : (
                <div className="file-preview">
                  {attachmentPreview && attachmentPreview.startsWith('data:image') ? (
                    <img src={attachmentPreview} alt="Preview" className="preview-image" />
                  ) : (
                    <div className="file-info">
                      <FontAwesomeIcon icon={faFileImage} />
                      <span>{attachmentFile.name}</span>
                    </div>
                  )}
                  <button type="button" className="remove-file-btn" onClick={removeAttachment}>
                    <FontAwesomeIcon icon={faTimes} /> {t('feedback.remove')}
                  </button>
                </div>
              )}
              <small className="help-text">{t('feedback.maxFileSize')}</small>
            </div>
          </div>

          <div className="system-info">
            <details>
              <summary>{t('feedback.systemInfo')}</summary>
              <div className="system-info-content">
                <div className="info-section">
                  <h4>{t('feedback.display')}</h4>
                  <p><strong>{t('feedback.screenResolution')}:</strong> {systemInfo.screenResolution}</p>
                  <p><strong>{t('feedback.viewportSize')}:</strong> {systemInfo.viewportSize}</p>
                  <p><strong>{t('feedback.colorDepth')}:</strong> {systemInfo.colorDepth}</p>
                </div>
                <div className="info-section">
                  <h4>{t('feedback.browser')}</h4>
                  <p><strong>{t('feedback.userAgent')}:</strong> {systemInfo.browser.substring(0, 80)}...</p>
                  <p><strong>{t('feedback.device')}:</strong> {systemInfo.device}</p>
                  <p><strong>{t('feedback.os')}:</strong> {systemInfo.os}</p>
                  <p><strong>{t('feedback.cookiesEnabled')}:</strong> {systemInfo.cookiesEnabled}</p>
                </div>
                <div className="info-section">
                  <h4>{t('feedback.locationLang')}</h4>
                  <p><strong>{t('feedback.language')}:</strong> {systemInfo.language}</p>
                  <p><strong>{t('feedback.timezone')}:</strong> {systemInfo.timezone}</p>
                </div>
                <div className="info-section">
                  <h4>{t('feedback.pageContext')}</h4>
                  <p><strong>{t('feedback.currentUrl')}:</strong> {systemInfo.url}</p>
                  <p><strong>{t('feedback.referrer')}:</strong> {systemInfo.referrer || 'Direct'}</p>
                  <p><strong>{t('feedback.onlineStatus')}:</strong> {systemInfo.onlineStatus}</p>
                </div>
              </div>
            </details>
          </div>

          <div className="feedback-modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel" disabled={isSubmitting}>
              {t('feedback.cancel')}
            </button>
            <button type="submit" className="btn-submit" disabled={isSubmitting}>
              {isSubmitting ? t('feedback.submitting') : t('feedback.submitBtn')}
            </button>
          </div>
        </form>
        </>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
